import React, { useCallback, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';

import { createDriver } from '../../services/driver';
import {
  ERROR_$,
  NEW_DRIVER_CREATED,
  PLEASE_FIX_ERRORS,
  SOMETHING_WENT_WRONG,
} from './messages';
import CreateDriverForm from './components/createDriverForm';
import { ValidationEffect } from './components/validationEffect';
import useLocationQueryParams from '../../hooks/useLocationQueryParams';
import useLoader from '../../hooks/useLoader';
import { SUBMIT_DRIVER_PROCESS } from '../../enums/loaderProcess';

const CreateDriverDialog = forwardRef(
  (
    {
      route = {},
      handleCreateNewDriver,
      showMessage,
      sections,
      vehicles,
      initialValues,
    },
    ref
  ) => {
    const { depot: depotCode, date } = useLocationQueryParams();

    const [showLoader, hideLoader] = useLoader();

    const {
      handleSubmit,
      control,
      formState: { isDirty, errors, isSubmitting },
    } = useForm({
      defaultValues: initialValues,
      mode: 'onSubmit',
    });

    useImperativeHandle(
      ref,
      () => ({
        isDirty,
      }),
      [isDirty]
    );

    const showValidationError = useCallback(() => {
      showMessage(PLEASE_FIX_ERRORS);
    }, [showMessage]);

    const handleSubmitDriver = useCallback(
      async (values) => {
        showLoader(SUBMIT_DRIVER_PROCESS);
        try {
          const { driverCode, password } = await createDriver({
            driver: values,
            depotCode,
            date,
            routeId: route.routeId,
          });
          showMessage(NEW_DRIVER_CREATED);
          handleCreateNewDriver({
            ...values,
            driverCode,
            isNewDriver: true,
            password,
          });
        } catch (e) {
          const message = e?.error?.message;
          showMessage(message ? ERROR_$(message) : SOMETHING_WENT_WRONG);
        } finally {
          hideLoader(SUBMIT_DRIVER_PROCESS);
        }
      },
      [
        showLoader,
        hideLoader,
        showMessage,
        date,
        depotCode,
        route.routeId,
        handleCreateNewDriver,
      ]
    );

    return (
      <form
        onSubmit={handleSubmit(handleSubmitDriver)}
        style={{ width: '100%' }}
        ref={ref}
      >
        <CreateDriverForm
          vehicles={vehicles}
          isSubmitting={isSubmitting}
          dirty={isDirty}
          sections={sections}
          control={control}
          showMessage={showMessage}
        />
        <ValidationEffect
          isSubmitting={isSubmitting}
          isValid={!Object.values(errors).length}
          callback={showValidationError}
        />
      </form>
    );
  }
);

CreateDriverDialog.displayName = 'CreateDriverDialog';

export default CreateDriverDialog;
